import { API_ENDPOINT, fetchWithAuth, parseApiResponse } from ".";

export type DiscountCodeUsageType = "single_use" | "multi_use";
export type DiscountCodeType = "percent" | "fixed";
export type DiscountCodeStatus = "active" | "scheduled" | "expired" | "used";

export interface AdminDiscountCode {
  id: number;
  eventId: number;
  code: string;
  usageType: DiscountCodeUsageType;
  discountType: DiscountCodeType;
  discountValue: number;
  ticketTypeIds: number[];
  startsAt: string | null;
  endsAt: string | null;
  redeemedCount: number;
  status: DiscountCodeStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountCodeInput {
  code: string;
  usageType: DiscountCodeUsageType;
  discountType: DiscountCodeType;
  discountValue: number;
  ticketTypeIds: number[];
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface UpdateDiscountCodeDatesInput {
  startsAt?: string | null;
  endsAt?: string | null;
}

export async function listDiscountCodes(
  eventId: number,
): Promise<AdminDiscountCode[]> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/discount-codes`,
  );
  const data = await parseApiResponse<{ discountCodes: AdminDiscountCode[] }>(
    response,
  );
  return data.discountCodes;
}

export async function createDiscountCode(
  eventId: number,
  input: CreateDiscountCodeInput,
): Promise<AdminDiscountCode> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/discount-codes`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  const data = await parseApiResponse<{ discountCode: AdminDiscountCode }>(
    response,
  );
  return data.discountCode;
}

export async function updateDiscountCodeDates(
  eventId: number,
  id: number,
  input: UpdateDiscountCodeDatesInput,
): Promise<AdminDiscountCode> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/discount-codes/${id}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  const data = await parseApiResponse<{ discountCode: AdminDiscountCode }>(
    response,
  );
  return data.discountCode;
}

export async function deleteDiscountCode(
  eventId: number,
  id: number,
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${eventId}/discount-codes/${id}`,
    { method: "DELETE" },
  );
  await parseApiResponse<{ message: string }>(response);
}

export interface DiscountCodePreview {
  discountType: DiscountCodeType;
  discountValue: number;
  ticketTypeIds: number[];
}

/** Public — used by the registration form to preview a code before submitting. */
export async function validateDiscountCode(
  eventSlug: string,
  code: string,
): Promise<DiscountCodePreview> {
  const params = new URLSearchParams({ code });
  const response = await fetch(
    `${API_ENDPOINT}/events/${eventSlug}/discount-codes/validate?${params.toString()}`,
  );
  const data = await parseApiResponse<{ discountCode: DiscountCodePreview }>(
    response,
  );
  return data.discountCode;
}

export interface PricedComponent {
  ticketTypeId: number | null;
  pence: number;
}

/**
 * Client-side estimate of what a code will discount, mirroring the backend's
 * eligible-subtotal formula (percent of / fixed capped at the sum of eligible
 * components) so this preview matches what checkout will actually charge.
 */
export function estimateDiscountPence(
  components: PricedComponent[],
  preview: DiscountCodePreview | null,
): number {
  if (!preview) return 0;
  const eligibleIds = new Set(preview.ticketTypeIds);
  const eligibleSum = components
    .filter((c) => c.ticketTypeId !== null && eligibleIds.has(c.ticketTypeId))
    .reduce((sum, c) => sum + c.pence, 0);
  if (eligibleSum <= 0) return 0;
  return preview.discountType === "percent"
    ? Math.round((eligibleSum * preview.discountValue) / 100)
    : Math.min(preview.discountValue, eligibleSum);
}
